/**
 * Currency and Financial Formatting Utilities
 *
 * Provides consistent monetary and percentage formatting across the application.
 * Used in work order billing, invoicing, and financial summary views.
 * All monetary values use the en-US locale for number formatting.
 */

/**
 * Formats a numeric amount as a currency string.
 *
 * Uses the Intl.NumberFormat API to ensure proper locale-aware formatting,
 * including currency symbols, thousands separators, and decimal places.
 * Always displays exactly 2 decimal places for consistency in financial displays.
 *
 * @param amount - The numeric dollar amount to format (e.g., 1500.5)
 * @param currency - ISO 4217 currency code; defaults to "USD"
 * @returns A formatted currency string like "$1,500.50"
 *
 * @example
 * formatCurrency(1500.5)		   // "$1,500.50"
 * formatCurrency(0)			   // "$0.00"
 * formatCurrency(1500.5, 'EUR')   // "€1,500.50"
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amount);
}

/**
 * Compact currency formatter — drops the cents for headline numbers
 * (Dashboard cards, summary tiles). Same locale/currency conventions
 * as formatCurrency, just zero fraction digits.
 *
 * @param amount - The numeric dollar amount to format
 * @param currency - ISO 4217 currency code; defaults to "USD"
 * @returns A formatted currency string like "$1,501"
 *
 * @example
 * formatCurrencyCompact(1500.5)   // "$1,501"
 * formatCurrencyCompact(0)        // "$0"
 */
export function formatCurrencyCompact(amount: number, currency: string = 'USD'): string {
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency,
		maximumFractionDigits: 0,
	}).format(amount);
}

/**
 * Null-safe wrapper around formatCurrency. Accepts strings (parsed as
 * floats), numbers, null, and undefined. Returns null when the input
 * isn't a finite number — callers can render an em-dash or skip the
 * field entirely. Used by surfaces like the EstimateBadge that may
 * not have a value yet.
 *
 * @param value - Numeric or string-encoded amount, or nullish
 * @param currency - ISO 4217 currency code; defaults to "USD"
 * @returns Formatted currency string, or null when the input is invalid
 *
 * @example
 * formatCurrencyOrNull(1500.5)   // "$1,500.50"
 * formatCurrencyOrNull("12.5")   // "$12.50"
 * formatCurrencyOrNull(null)     // null
 * formatCurrencyOrNull("")       // null
 */
export function formatCurrencyOrNull(
	value: number | string | null | undefined,
	currency: string = 'USD',
): string | null {
	if (value === null || value === undefined || value === '') return null;
	const n = typeof value === 'string' ? parseFloat(value) : value;
	if (!Number.isFinite(n)) return null;
	return formatCurrency(n, currency);
}

/**
 * Formats a decimal tax rate as a human-readable percentage string.
 *
 * Tax rates are stored in the database as decimals (e.g., 0.07 for 7%),
 * so this function multiplies by 100 and appends the "%" symbol.
 * Always displays exactly 2 decimal places for precision.
 *
 * @param rate - The decimal tax rate (e.g., 0.07 for 7%, 0.0725 for 7.25%)
 * @returns A formatted percentage string like "7.00%" or "7.25%"
 *
 * @example
 * formatTaxRate(0.07)	  // "7.00%"
 * formatTaxRate(0.0725)  // "7.25%"
 */
export function formatTaxRate(rate: number): string {
	return `${(rate * 100).toFixed(2)}%`;
}
